import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import {
  Check,
  X as XIcon,
  ArrowRight,
  Sparkles,
  Zap,
  Building2,
  Rocket,
  ShieldCheck,
  Layers,
  Clock,
} from "lucide-react";
import { COMING_SOON_BY_PLAN } from "@/lib/comingSoon";

// Public /pricing landing page (SEO P1). Real Postmoro plan values
// only — copy is cross-checked against the marketing pricing block
// on /ai-social-media-management. Do NOT invent benefits, features,
// or comparisons.
const PLANS = [
  {
    id: "free",
    name: "Free Trial",
    icon: Sparkles,
    price: "$0",
    period: "month",
    tagline: "Try Postmoro end-to-end at no cost.",
    features: [
      "10 AI generations / month",
      "5 published posts / month",
      "All 4 social platforms",
      "No credit card required",
    ],
    cta: "Start free",
    accent: "teal",
  },
  {
    id: "starter",
    name: "Starter",
    icon: Rocket,
    price: "$19.99",
    period: "month",
    tagline: "For solo founders posting a few times a week.",
    features: [
      "50 AI generations / month",
      "25 published posts / month",
      "All 4 social platforms",
      "Content calendar",
    ],
    cta: "Start free",
    accent: "teal",
  },
  {
    id: "pro",
    name: "Pro",
    icon: Zap,
    highlight: true,
    price: "$79.99",
    period: "month",
    tagline: "For small businesses ready to post consistently.",
    features: [
      "200 AI generations / month",
      "60 published posts / month",
      "All social platforms",
      "Marketing campaign ideas",
      "Drag-and-drop calendar",
      "Priority email + chat support",
    ],
    cta: "Start free",
    accent: "primary",
  },
  {
    id: "business",
    name: "Business",
    icon: Building2,
    price: "$249.99",
    period: "month",
    tagline: "For agencies and teams running high-volume brands.",
    features: [
      "1,000 AI generations / month",
      "Unlimited published posts (fair-use)",
      "Teams — 3 team seats included",
      "Multi-platform Autopilot batching",
      "Business Media Library (30 reusable images)",
      "Custom brand voice",
      "Priority support + onboarding",
    ],
    cta: "Start free",
    accent: "premium",
  },
];

// Truthful comparison rows sourced ONLY from the plan constants above.
// Comparison rows — SOURCE OF TRUTH:
//   - server.py::PLAN_AI_GENERATION_LIMITS   (usage cap for AI generations)
//   - server.py::PLAN_AI_POST_LIMITS         (usage cap for published posts)
//   - server.py::PLATFORM_LIMITS             (all None → all 4 platforms on every plan)
//   - server.py::MEDIA_LIBRARY_MAX_ITEMS=30  (universal cap — every plan)
//   - server.py::WORKSPACE_MAX_SEATS=3       (Business-only Teams cap)
//   - business_workspaces.py::BUSINESS_INCLUDED_WORKSPACES=4 (Business-only Multi-Business cap)
// Coming Soon items are NEVER represented in this table — they live in the
// per-card COMING_SOON_BY_PLAN section only.
//
// Row shapes accepted by the renderer below:
//   { section: "…" }                         → section header (full-width divider)
//   { label: "…", allPlans: true }           → single "Included on every plan" span
//   { label: "…", values: [...4 cells] }     → per-plan cells (bool | string)
const COMPARE_ROWS = [
  // -------- Usage limits (real caps) --------
  { section: "Usage limits" },
  { label: "AI generations / month",  values: ["10", "50", "200", "1,000"] },
  { label: "Published posts / month", values: ["5",  "25", "60",  "Unlimited (fair-use)"] },

  // -------- Included on every plan --------
  { section: "Included on every plan" },
  { label: "Facebook · Instagram · LinkedIn · X publishing",   allPlans: true },
  { label: "Content Calendar + drag-and-drop rescheduling",    allPlans: true },
  { label: "AI Generate Post (captions, hashtags, images)",    allPlans: true },
  { label: "Autopilot (draft · schedule · rotate)",            allPlans: true },
  { label: "Brand Learning + saved brand voice",               allPlans: true },
  { label: "Marketing Campaign Ideas",                         allPlans: true },
  { label: "Analytics",                                        allPlans: true },
  { label: "Media Library (up to 30 images)",                  allPlans: true },
  { label: "Business Profile + Connections",                   allPlans: true },

  // -------- Business-only (real code-gated entitlements) --------
  { section: "Business-only" },
  { label: "Teams — 3 team seats included",       values: [false, false, false, "Included"] },

  // -------- Support tier (service commitment, not a code-gated entitlement) --------
  { section: "Support tier" },
  { label: "Support tier", values: ["Email", "Email", "Priority email + chat", "Priority + onboarding"] },
];

const FAQS = [
  {
    q: "Can I try Postmoro without a credit card?",
    a: "Yes — the Free Trial plan lets you connect your social accounts and generate up to 10 posts a month without entering payment details.",
  },
  {
    q: "Which plan includes team collaboration?",
    a: "Team collaboration is a Business-plan feature. The Business plan includes 3 team seats so multiple people can draft, review, and approve content inside one Postmoro workspace with per-seat roles.",
  },
  {
    q: "Do all plans include every social platform?",
    a: "Yes. Every plan can publish to Facebook Pages, Instagram professional accounts, LinkedIn personal profiles, and X. Higher plans raise the monthly AI-generation and publish limits.",
  },
  {
    q: "Can I switch plans later?",
    a: "You can upgrade or downgrade at any time from Billing inside Postmoro. Your existing scheduled posts and connected accounts stay in place.",
  },
];

/* Small helper: render a compare cell (bool → check/dash, string → text). */
function CompareCell({ value, isPro }) {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <Check className={"h-4 w-4 " + (isPro ? "text-[color:var(--brand-primary)]" : "text-[color:var(--success)]")} />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center text-[color:var(--text-3)]">
        <XIcon className="h-3.5 w-3.5 opacity-50" />
      </div>
    );
  }
  return (
    <div className={"text-center " + (isPro ? "text-[color:var(--brand-primary)] font-medium" : "text-[color:var(--text-1)]")}>
      {value}
    </div>
  );
}

export default function PricingPage() {
  return (
    <>
      <SEO
        title="Postmoro Pricing | Plans for AI Social Media Management"
        description="Compare Postmoro plans for AI social media management, scheduling, automation, teams, and more. Start free — no credit card required."
        path="/pricing"
      />
      <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden" data-testid="pricing-page">
        {/* Ambient background — teal + cool blue radial glows, subtle grid. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0">
          <div
            className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[720px] rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.20), transparent 70%)" }}
          />
          <div
            className="absolute top-[380px] -right-40 h-[420px] w-[520px] rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(47,143,229,0.14), transparent 70%)" }}
          />
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.10) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "radial-gradient(ellipse at 50% 8%, black 30%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse at 50% 8%, black 30%, transparent 70%)",
            }}
          />
        </div>

        <MarketingHeader activePath="/pricing" />

        {/* ============ HERO ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              Pricing
            </div>
            <h1
              className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]"
              data-testid="pricing-hero-title"
            >
              Postmoro pricing, plain and simple.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              Every Postmoro plan includes AI content generation, cross-platform
              publishing, and the same core scheduling tools. Higher plans raise
              the monthly limits, and Business adds team collaboration.
            </p>

            {/* Trust pill row — real, no invented numbers. */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {[
                { icon: ShieldCheck, label: "No credit card required" },
                { icon: Layers,     label: "4 platforms · Facebook · Instagram · LinkedIn · X" },
                { icon: Zap,        label: "Cancel or switch plans anytime" },
              ].map((t) => (
                <span
                  key={t.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-3 py-1 text-[11px] text-[color:var(--text-2)]"
                >
                  <t.icon className="h-3 w-3 text-[color:var(--brand-primary)]" />
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ============ PLAN CARDS ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((p, i) => {
              const isPro = p.highlight;
              const isBusiness = p.accent === "premium";
              const cardBase =
                "relative rounded-2xl border p-5 flex flex-col overflow-hidden transition-shadow";
              const cardTone = isPro
                ? "border-[color:rgba(27,168,159,0.60)] bg-[color:var(--surface-1)] shadow-[0_0_0_1px_rgba(27,168,159,0.25),0_20px_50px_-24px_rgba(27,168,159,0.55)]"
                : isBusiness
                ? "border-[color:rgba(34,211,238,0.45)] bg-[color:var(--surface-1)]"
                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]";
              return (
                <div
                  key={p.id}
                  className={cardBase + " " + cardTone}
                  data-testid={`pricing-card-${p.id}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {/* Decorative radial glow — Pro gets teal, Business gets cyan. */}
                  {(isPro || isBusiness) && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full"
                      style={{
                        background: isPro
                          ? "radial-gradient(closest-side, rgba(27,168,159,0.25), transparent 70%)"
                          : "radial-gradient(closest-side, rgba(34,211,238,0.20), transparent 70%)",
                      }}
                    />
                  )}

                  {/* Header row: icon + name + POPULAR pill */}
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={
                          "h-8 w-8 rounded-[10px] flex items-center justify-center border " +
                          (isPro
                            ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)]"
                            : isBusiness
                            ? "border-[color:rgba(34,211,238,0.45)] bg-[color:rgba(34,211,238,0.10)] text-[color:var(--brand-secondary)]"
                            : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)]")
                        }
                      >
                        <p.icon className="h-4 w-4" />
                      </div>
                      <div className="font-display text-lg font-semibold">{p.name}</div>
                    </div>
                    {isPro && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-semibold">
                        Popular
                      </span>
                    )}
                    {isBusiness && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[color:rgba(34,211,238,0.45)] bg-[color:rgba(34,211,238,0.10)] text-[color:var(--brand-secondary)] font-semibold">
                        Premium
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="relative mt-4 flex items-baseline gap-1">
                    <span className="font-display text-3xl font-semibold tracking-tight">
                      {p.price}
                    </span>
                    <span className="text-xs text-[color:var(--text-3)]">/ {p.period}</span>
                  </div>
                  <p className="relative mt-2 text-xs text-[color:var(--text-3)] leading-relaxed min-h-[36px]">
                    {p.tagline}
                  </p>

                  {/* Divider */}
                  <div className="relative my-4 h-px bg-[color:var(--border-subtle)]" />

                  {/* Features */}
                  <ul className="relative space-y-2 text-sm text-[color:var(--text-2)] flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="inline-flex items-start gap-2">
                        <Check
                          className={
                            "h-4 w-4 shrink-0 mt-0.5 " +
                            (isPro
                              ? "text-[color:var(--brand-primary)]"
                              : isBusiness
                              ? "text-[color:var(--brand-secondary)]"
                              : "text-[color:var(--success)]")
                          }
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Coming Soon — plan-specific, sourced from
                      COMING_SOON_BY_PLAN (single source of truth). We strip
                      the trailing " — Coming Soon" suffix here because the
                      section itself is labeled COMING SOON, so we don't
                      double-label each row. */}
                  {(COMING_SOON_BY_PLAN[p.id] || []).length > 0 && (
                    <div
                      className="relative mt-4 pt-3 border-t border-dashed border-[color:var(--border-subtle)]"
                      data-testid={`pricing-coming-soon-${p.id}`}
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--brand-primary)] mb-2">
                        <Clock className="h-3 w-3" />
                        Coming Soon
                      </div>
                      <ul className="space-y-1.5 text-xs text-[color:var(--text-3)]">
                        {COMING_SOON_BY_PLAN[p.id].map((raw) => {
                          const label = raw.replace(/\s*—\s*Coming Soon\s*$/i, "");
                          return (
                            <li key={raw} className="inline-flex items-start gap-2">
                              <Clock className="h-3 w-3 shrink-0 mt-0.5 text-[color:var(--brand-primary)] opacity-70" />
                              <span className="leading-snug">{label}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* CTA */}
                  <Link to="/login" className="relative mt-5">
                    <Button
                      className={
                        "w-full font-semibold " +
                        (isPro
                          ? "bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                          : isBusiness
                          ? "bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-3)] border border-[color:rgba(34,211,238,0.45)] text-[color:var(--text-1)]"
                          : "border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]")
                      }
                      data-testid={`pricing-cta-${p.id}`}
                    >
                      {p.cta} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============ PLAN COMPARISON TABLE ============ */}
        <section
          className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14"
          data-testid="pricing-compare-section"
        >
          <p className="eyebrow text-[color:var(--brand-primary)]">Compare</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            All Postmoro plans, side-by-side
          </h2>
          <p className="mt-2 text-sm text-[color:var(--text-3)] max-w-2xl">
            Every plan publishes to the same four social networks and gets the
            full Postmoro toolkit. Higher plans raise the monthly limits; the
            Business plan adds team collaboration.
          </p>

          {/* Desktop grid table */}
          <div className="mt-6 hidden md:block rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] overflow-hidden">
            <div className="grid grid-cols-5 text-sm">
              <div className="px-4 py-3 text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] border-b border-[color:var(--border-subtle)]">
                Feature
              </div>
              {PLANS.map((p) => (
                <div
                  key={p.id}
                  className={
                    "px-4 py-3 text-center border-b border-[color:var(--border-subtle)] " +
                    (p.highlight
                      ? "bg-[color:rgba(27,168,159,0.06)] text-[color:var(--brand-primary)] font-semibold"
                      : "text-[color:var(--text-2)]")
                  }
                >
                  {p.name}
                </div>
              ))}

              {COMPARE_ROWS.map((row, idx) => {
                // Section header — full-width divider row.
                if (row.section) {
                  return (
                    <div
                      key={`sec-${idx}`}
                      className="col-span-5 px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)] bg-[color:var(--surface-2)] border-b border-[color:var(--border-subtle)]"
                    >
                      {row.section}
                    </div>
                  );
                }
                // "Included on every plan" — single spanning row with a subtle teal
                // strip so the visual balance stays premium.
                if (row.allPlans) {
                  return (
                    <React.Fragment key={row.label}>
                      <div className="px-4 py-3 text-[color:var(--text-2)] border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]">
                        {row.label}
                      </div>
                      <div className="px-4 py-3 col-span-4 border-b border-[color:var(--border-subtle)] bg-[color:rgba(27,168,159,0.04)] text-[color:var(--brand-primary)] text-xs text-center inline-flex items-center justify-center gap-1.5">
                        <Check className="h-3.5 w-3.5" />
                        Included on every plan
                      </div>
                    </React.Fragment>
                  );
                }
                // Per-plan cells.
                return (
                  <React.Fragment key={row.label}>
                    <div className="px-4 py-3 text-[color:var(--text-2)] border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]">
                      {row.label}
                    </div>
                    {PLANS.map((p, i) => (
                      <div
                        key={p.id}
                        className={
                          "px-4 py-3 border-b border-[color:var(--border-subtle)] text-xs " +
                          (p.highlight ? "bg-[color:rgba(27,168,159,0.04)]" : "")
                        }
                      >
                        <CompareCell value={row.values[i]} isPro={p.highlight} />
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Mobile: stacked per-plan feature lists (avoids tiny table text). */}
          <div className="mt-6 md:hidden space-y-3">
            {PLANS.map((p, pi) => (
              <div
                key={p.id}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-base font-semibold">{p.name}</div>
                  <div className="text-sm text-[color:var(--text-3)]">{p.price} / {p.period}</div>
                </div>
                <ul className="mt-3 space-y-1.5 text-xs">
                  {COMPARE_ROWS.map((row, idx) => {
                    if (row.section) {
                      return (
                        <li
                          key={`sec-${idx}`}
                          className="mt-2 pt-2 border-t border-[color:var(--border-subtle)] text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]"
                        >
                          {row.section}
                        </li>
                      );
                    }
                    if (row.allPlans) {
                      return (
                        <li key={row.label} className="flex items-start gap-2 text-[color:var(--text-2)]">
                          <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[color:var(--brand-primary)]" />
                          <span>
                            <span className="text-[color:var(--text-3)]">{row.label}: </span>
                            <span className="text-[color:var(--text-1)]">Included</span>
                          </span>
                        </li>
                      );
                    }
                    const display = row.values[pi];
                    if (display === false) return null;
                    return (
                      <li key={row.label} className="flex items-start gap-2 text-[color:var(--text-2)]">
                        <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[color:var(--brand-primary)]" />
                        <span>
                          <span className="text-[color:var(--text-3)]">{row.label}: </span>
                          <span className="text-[color:var(--text-1)]">
                            {display === true ? "Included" : display}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* Truthful footnotes below the table. */}
          <p className="mt-4 text-[11px] text-[color:var(--text-3)] leading-relaxed max-w-2xl">
            The Support tier row is a service commitment, not a product entitlement — every plan can email support@postmoro.com; higher tiers get faster response and onboarding help.
          </p>
        </section>

        {/* ============ EXPLORE CARDS ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <p className="eyebrow text-[color:var(--brand-primary)]">Explore</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            What's included in Postmoro
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-[color:var(--text-2)]">
            <Link
              to="/features"
              className="group rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 hover:border-[color:rgba(27,168,159,0.45)] transition-colors"
              data-testid="pricing-explore-features"
            >
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">All features</div>
              <p className="mt-2 leading-relaxed">See every Postmoro capability — AI content, Autopilot, scheduling, Brand Learning, Teams, and more.</p>
              <div className="mt-3 text-xs text-[color:var(--brand-primary)] inline-flex items-center gap-1">
                Browse features
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
            <Link
              to="/features/autopilot"
              className="group rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 hover:border-[color:rgba(27,168,159,0.45)] transition-colors"
              data-testid="pricing-explore-autopilot"
            >
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">Autopilot</div>
              <p className="mt-2 leading-relaxed">Postmoro drafts, schedules, and rotates content categories automatically — with approval before posting by default.</p>
              <div className="mt-3 text-xs text-[color:var(--brand-primary)] inline-flex items-center gap-1">
                See Autopilot
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
            <Link
              to="/features/brand-learning"
              className="group rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 hover:border-[color:rgba(27,168,159,0.45)] transition-colors"
              data-testid="pricing-explore-brand-learning"
            >
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">Brand Learning</div>
              <p className="mt-2 leading-relaxed">Teach Postmoro your existing social voice so new posts match how your business already writes.</p>
              <div className="mt-3 text-xs text-[color:var(--brand-primary)] inline-flex items-center gap-1">
                See Brand Learning
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section
          className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14"
          data-testid="pricing-faq-section"
        >
          <p className="eyebrow text-[color:var(--brand-primary)]">FAQ</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Pricing FAQ
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {FAQS.map((item, idx) => (
              <div
                key={item.q}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
                data-testid={`pricing-faq-${idx}`}
              >
                <h3 className="font-display text-base font-semibold">{item.q}</h3>
                <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ============ CLOSING CTA ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pb-16">
          <div className="relative rounded-2xl overflow-hidden border border-[color:rgba(27,168,159,0.35)] bg-[color:var(--surface-1)] p-8 sm:p-10 text-center">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 0%, rgba(27,168,159,0.20), transparent 60%), radial-gradient(ellipse at 100% 100%, rgba(47,143,229,0.16), transparent 60%)",
              }}
            />
            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Start free — every plan includes a 10-post month at no cost
              </h2>
              <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
                Connect a network, generate your first post, and see how Postmoro
                sounds when it's writing for your business. Upgrade only if it
                actually saves you time.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/login">
                  <Button
                    size="lg"
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                    data-testid="pricing-closing-cta"
                  >
                    Start free <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]"
                  >
                    Talk to us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
